<template>
  <div>
    <material-button
      class="d-none"
      data-bs-toggle="modal"
      data-bs-target="#modalPriceSchedules"
      id="show-modal-price-schedules"
    ></material-button>
    <div class="modal fade" id="modalPriceSchedules" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Special Scheduled Price</h5>
            <i
              class="material-icons btn-close-modal justify-content-end cursor-pointer"
              data-bs-dismiss="modal"
              aria-label="Close"
              >clear</i
            >
          </div>
          <div class="modal-body">
            <div class="ps-master">
              <div>
                <div class="ps-master__title">Enable Special Scheduled Price</div>
                <div class="ps-master__sub">
                  When off, the schedules below are kept but not applied.
                </div>
              </div>
              <div class="form-check form-switch mb-0 ms-auto">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="price_schedules_enabled"
                  :checked="enabled"
                  @change="enabled = $event.target.checked"
                />
                <label class="form-check-label mb-0" for="price_schedules_enabled">
                  {{ enabled ? "On" : "Off" }}
                </label>
              </div>
            </div>

            <div class="ps-hint">
              <i class="material-icons ps-hint__icon">info</i>
              <div>
                Inside a switched-on schedule (its dates, days and hours, Singapore time GMT+8),
                everyone (members and non-members) pays the scheduled price level for Main Items;
                there is no separate member price. A price is never $0: an item with no price at the
                scheduled level keeps its usual Default and Member prices. If schedules overlap,
                the one higher in the list applies.
              </div>
            </div>

            <div v-if="saveError" class="alert alert-danger text-white py-2 mb-3 ps-error">
              {{ saveError }}
            </div>

            <div v-if="!rows.length" class="ps-empty">No price schedules yet.</div>

            <div :class="{ 'ps-list--off': !enabled }">
            <div
              v-for="(schedule, index) in rows"
              :key="index"
              class="ps-card"
              :class="{ 'ps-card--off': !schedule.status, 'ps-card--invalid': errors[index] }"
            >
              <div class="ps-card__head">
                <span class="ps-card__title">Schedule {{ index + 1 }}</span>
                <span class="ps-card__move">
                  <i
                    class="material-icons"
                    :class="{ 'ps-card__move--off': index === 0 }"
                    title="Move up (higher wins)"
                    @click="move(index, -1)"
                    >arrow_upward</i
                  >
                  <i
                    class="material-icons"
                    :class="{ 'ps-card__move--off': index === rows.length - 1 }"
                    title="Move down"
                    @click="move(index, 1)"
                    >arrow_downward</i
                  >
                </span>
                <div class="form-check form-switch mb-0 ms-auto">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="'price_schedule_status_' + index"
                    :checked="!!schedule.status"
                    @change="schedule.status = $event.target.checked ? 1 : 0"
                  />
                  <label class="form-check-label mb-0" :for="'price_schedule_status_' + index">
                    {{ schedule.status ? "On" : "Off" }}
                  </label>
                </div>
                <i
                  class="material-icons ps-card__remove"
                  title="Remove schedule"
                  @click="rows.splice(index, 1)"
                  >delete_outline</i
                >
              </div>

              <div class="ps-card__body">
                <ScheduleWhenFields
                  :schedule="schedule"
                  :id-prefix="'price_schedule_' + index"
                  @update="Object.assign(schedule, $event)"
                />
                <div class="ps-grid ps-grid--price">
                  <div class="ps-field">
                    <div class="ps-field__label">Scheduled price</div>
                    <Multiselect
                      v-model="schedule.price_key"
                      :options="defaultPrices"
                      placeholder="Select"
                      :canClear="false"
                      class="ps-select"
                    />
                  </div>
                </div>

                <div class="text-danger ps-error" v-if="errors[index]">{{ errors[index] }}</div>
              </div>
            </div>

            <button
              type="button"
              class="ps-add"
              :disabled="rows.length >= max"
              @click="addRow"
            >
              <i class="material-icons">add</i> Add schedule
            </button>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" data-bs-dismiss="modal">
              Cancel
            </button>
            <material-button class="btn btn-sm btn-background mb-0" :disabled="saving" @click="apply">
              {{ saving ? "Saving..." : "Save" }}
            </material-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialButton from "@/components/MaterialButton.vue";
import Multiselect from "@vueform/multiselect";
import SettingStoreService from "../services/settingStore.service";
import { isUnrestrictedWhen, newScheduleWhen, scheduleWhenError, scheduleWhenFrom, scheduleWhenPayload, PRICE_SCHEDULE_MAX } from "@/config/priceSchedule";
import ScheduleWhenFields from "@/components/ScheduleWhenFields.vue";

// Special Scheduled Price editor (switch + schedules). Works on a copy: Save checks it (same
// rules as the API) and saves it straight away for this channel; Cancel drops it.
export default {
  name: "ModalPriceSchedules",
  components: { MaterialButton, Multiselect, ScheduleWhenFields },
  props: {
    prices: { type: Array, default: () => [] },
    branchId: { type: [Number, String], default: 0 },
  },
  emits: ["saved"],
  data() {
    return {
      rows: [],
      // Master switch (minty_branches.price_schedules_enabled), saved together with the rows.
      enabled: false,
      // Incomplete rows are flagged only after Apply; clashes show right away.
      checked: false,
      max: PRICE_SCHEDULE_MAX,
      saving: false,
      saveError: "",
    };
  },
  computed: {
    // Price 2..9: Price 1 is always on, and the page's list may also hold the Price List's
    // Member "None" entry, which a schedule does not offer.
    defaultPrices() {
      return this.prices.filter((price) => price.value && price.value !== "price_1");
    },
    // One message (or "") per row; missing fields only after Save.
    errors() {
      return this.rows.map((schedule) => {
        const when = scheduleWhenError(schedule);
        if (when) {
          return when;
        }
        if (this.checked && !schedule.price_key) {
          return "Pick a price level.";
        }
        if (this.checked && isUnrestrictedWhen(schedule)) {
          return "Set dates, days or hours, or this price would apply all the time.";
        }
        return "";
      });
    },
  },
  methods: {
    open(schedules, enabled) {
      this.enabled = Number(enabled) === 1;
      this.rows = (schedules || []).map((schedule) => ({
        ...schedule,
        ...scheduleWhenFrom(schedule),
        price_key: schedule.price_key || "",
      }));
      this.checked = false;
      this.saveError = "";
      document.getElementById("show-modal-price-schedules").click();
    },
    addRow() {
      this.rows.push({ ...newScheduleWhen(), price_key: "", status: 1 });
    },
    // Overlapping schedules: the one higher in the list wins, so the order is the priority.
    move(index, step) {
      const target = index + step;
      if (target < 0 || target >= this.rows.length) {
        return;
      }
      const [row] = this.rows.splice(index, 1);
      this.rows.splice(target, 0, row);
    },
    apply() {
      this.checked = true;
      this.saveError = "";
      if (this.errors.some(Boolean)) {
        return;
      }
      this.saving = true;
      SettingStoreService.savePriceSchedules(
        this.branchId,
        this.rows.map((schedule) => ({
          ...scheduleWhenPayload(schedule),
          price_key: schedule.price_key,
          status: schedule.status ? 1 : 0,
        })),
        this.enabled ? 1 : 0,
      ).then(
        (response) => {
          this.saving = false;
          if (response.data.result.isSuccess) {
            this.$emit("saved", response.data.data);
            document.querySelector("#modalPriceSchedules .btn-close-modal").click();
          } else {
            this.saveError = response.data.result.message;
          }
        },
        (error) => {
          this.saving = false;
          this.saveError = error?.response?.data?.result?.message || error.message;
        },
      );
    },
  },
};
</script>

<style scoped>
.ps-master {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #e3e6ea;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.ps-master__title {
  font-weight: 600;
  color: #344767;
}
.ps-master__sub {
  font-size: 13px;
  color: #7b809a;
}
.ps-list--off {
  opacity: 0.6;
}
.ps-hint {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #f4f6f9;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
  font-size: 13px;
  line-height: 1.45;
  color: #67748e;
}
.ps-hint__icon {
  font-size: 18px;
  color: #8a94a6;
}
.ps-empty {
  font-size: 14px;
  color: #7b809a;
  text-align: center;
  padding: 18px 0 8px;
}
.ps-error {
  font-size: 13px;
}
.ps-card {
  border: 1px solid #e3e6ea;
  border-radius: 10px;
  margin-bottom: 12px;
  overflow: visible;
}
.ps-card--off {
  background: #f8f9fa;
}
.ps-card--off .ps-card__body {
  opacity: 0.55;
}
.ps-card--invalid {
  border-color: #f44335;
}
.ps-card__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid #eef0f3;
}
.ps-card__title {
  font-weight: 600;
  color: #344767;
}
.ps-grid--price {
  margin-top: 12px;
}
.ps-card__move {
  display: inline-flex;
  gap: 2px;
  margin-left: 6px;
}
.ps-card__move .material-icons {
  font-size: 18px;
  color: #7b809a;
  cursor: pointer;
}
.ps-card__move .ps-card__move--off {
  opacity: 0.3;
  pointer-events: none;
}
.ps-card__remove {
  cursor: pointer;
  color: #9aa0ac;
  font-size: 20px;
}
.ps-card__remove:hover {
  color: #f44335;
}
.ps-card__body {
  padding: 12px 14px 14px;
  display: grid;
  gap: 14px;
}
.ps-field {
  display: grid;
  gap: 6px;
  min-width: 0;
}
.ps-field__label {
  font-size: 12px;
  font-weight: 600;
  color: #7b809a;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.ps-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}
.ps-datetime {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px;
  gap: 8px;
}
.ps-date :deep(.dp__input) {
  font-size: 14px;
  color: #344767;
  height: 40px;
}
@media (max-width: 767px) {
  .ps-grid {
    grid-template-columns: 1fr;
  }
}
.ps-select {
  margin: 0;
  width: 100%;
  min-height: 40px;
}
.ps-add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px dashed #c4cad3;
  border-radius: 10px;
  background: transparent;
  color: #344767;
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  justify-content: center;
  padding: 10px;
}
.ps-add:hover:not(:disabled) {
  background: #f4f6f9;
}
.ps-add:disabled {
  opacity: 0.5;
}
.ps-add .material-icons {
  font-size: 18px;
}
</style>
